
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useToast } from '@/hooks/use-toast';

interface XeroContextType {
  isConnected: boolean;
  connectionStatus: 'disconnected' | 'connected' | 'connecting' | 'error';
  lastSynced: Date | null;
  connect: (clientId: string, clientSecret: string) => Promise<void>;
  disconnect: () => Promise<void>;
  syncData: () => Promise<void>;
}

const XeroContext = createContext<XeroContextType | undefined>(undefined);

interface XeroProviderProps {
  children: ReactNode;
}

export function XeroProvider({ children }: XeroProviderProps) {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connected' | 'connecting' | 'error'>('disconnected');
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const { toast } = useToast();

  // Load stored connection state on mount
  useEffect(() => {
    try {
      const storedState = localStorage.getItem('xero_connection');
      if (storedState) {
        const { isConnected, lastSynced } = JSON.parse(storedState);
        setIsConnected(isConnected);
        setConnectionStatus(isConnected ? 'connected' : 'disconnected');
        setLastSynced(lastSynced ? new Date(lastSynced) : null);
      }
    } catch (error) {
      console.error('Failed to load Xero connection state:', error);
    }
  }, []);

  // Save connection state to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('xero_connection', JSON.stringify({
        isConnected,
        lastSynced: lastSynced?.toISOString()
      }));
    } catch (error) {
      console.error('Failed to save Xero connection state:', error);
    }
  }, [isConnected, lastSynced]);

  const connect = async (clientId: string, clientSecret: string): Promise<void> => {
    try {
      setConnectionStatus('connecting');
      
      // In a real implementation, this would:
      // 1. Generate an OAuth URL with the clientId
      // 2. Redirect the user to Xero for authentication
      // 3. Handle the callback in the XeroCallback component
      
      // For demo purposes, we're simulating a successful connection
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setIsConnected(true);
      setConnectionStatus('connected');
      toast({
        title: 'Connected to Xero',
        description: 'Your Xero account has been successfully connected',
      });
    } catch (error) {
      console.error('Failed to connect to Xero:', error);
      setConnectionStatus('error');
      toast({
        title: 'Connection failed',
        description: 'Failed to connect to Xero. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const disconnect = async (): Promise<void> => {
    try {
      // In a real implementation, this would revoke the OAuth tokens
      
      // For demo purposes, we're simulating a successful disconnection
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsConnected(false);
      setConnectionStatus('disconnected');
      setLastSynced(null);
      toast({
        title: 'Disconnected from Xero',
        description: 'Your Xero account has been disconnected',
      });
    } catch (error) {
      console.error('Failed to disconnect from Xero:', error);
      toast({
        title: 'Disconnection failed',
        description: 'Failed to disconnect from Xero. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const syncData = async (): Promise<void> => {
    if (!isConnected) {
      toast({
        title: 'Not connected',
        description: 'Please connect to Xero before syncing data',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      // In a real implementation, this would sync data between systems
      
      // For demo purposes, we're simulating a successful sync
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const now = new Date();
      setLastSynced(now);
      toast({
        title: 'Data synchronized',
        description: 'Your data has been successfully synchronized with Xero',
      });
    } catch (error) {
      console.error('Failed to sync data with Xero:', error);
      toast({
        title: 'Sync failed',
        description: 'Failed to sync data with Xero. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const value = {
    isConnected,
    connectionStatus,
    lastSynced,
    connect,
    disconnect,
    syncData,
  };

  return <XeroContext.Provider value={value}>{children}</XeroContext.Provider>;
}

export const useXero = (): XeroContextType => {
  const context = useContext(XeroContext);
  if (context === undefined) {
    throw new Error('useXero must be used within a XeroProvider');
  }
  return context;
};
